import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Hidden from '@material-ui/core/Hidden';
import CreateTransactionDrawer from '../CreateTransactionDrawer';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { transactions as transactionsApi } from '@afosto/order/api/Transactions';
import { transactionFormatter } from './formatters';
import translations from './translations';
import TransactionListItem from '../TransactionListItem';

const PaymentsSection = ({ entity, amount, description, client, allowCreateTransaction }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:transactions:create']);
  const [showCreateTransaction, setShowCreateTransaction] = useState(false);
  const { type: entityType, id: entityId } = entity ?? {};

  const fetchTransactions = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) => {
        if (!entityType || !entityId) {
          return [];
        }

        return transactionsApi.listTransactions(
          {
            entityId,
            entityType,
          },
          requestOptions,
        );
      },
      { transformData: data => data },
    );

  const dataSource = useDataGrid(fetchTransactions, {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'transaction',
        },
        {
          key: 'amount_due',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.amountDue',
            defaultMessage: 'Amount due',
            description: 'The column name for column amount due',
          }),
          type: 'currency',
        },
        {
          key: 'amount_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.amountPaid',
            defaultMessage: 'Amount paid',
            description: 'The column name for column amount paid',
          }),
          type: 'currency',
        },
        {
          key: 'is_paid',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.isPaid',
            defaultMessage: 'Paid',
            description: 'The column name for column is paid',
          }),
          type: 'boolean',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.transactions.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'date',
        },
      ],
    },
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    const { current: currentDataSource } = dataSourceRef;

    if (entityType && entityId) {
      currentDataSource.reloadDataSource();
    }
  }, [entityId, entityType]);

  /**
   * Handler that fires after creating the transaction.
   */
  const handleCreateTransaction = () => {
    setShowCreateTransaction(false);
    dataSource.reloadDataSource();
  };

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
        action={
          allowCreateTransaction && (
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setShowCreateTransaction(true)}
            >
              {intl.formatMessage(translations.button.create.label)}
            </Button>
          )
        }
      />
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={transaction => (
            <TransactionListItem transaction={transaction} key={transaction?.id} />
          )}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid dataSource={dataSource} formatters={{ transaction: transactionFormatter }} />
      </Hidden>
      <CreateTransactionDrawer
        isOpen={showCreateTransaction}
        onClose={() => setShowCreateTransaction(false)}
        onCreate={handleCreateTransaction}
        amount={amount}
        description={description}
        client={client}
        entity={entity}
      />
    </Card>
  );
};

PaymentsSection.propTypes = {
  entity: PropTypes.object,
  amount: PropTypes.number,
  description: PropTypes.string,
  client: PropTypes.object,
  allowCreateTransaction: PropTypes.bool,
};

PaymentsSection.defaultProps = {
  entity: null,
  amount: null,
  description: null,
  client: undefined,
  allowCreateTransaction: false,
};

export default PaymentsSection;
